import moment from 'moment'
import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import {
  applyTraceHeader,
  captureTraceFromResponse,
} from '../utils/reservationTrace'
import { clearAuthSession } from '../utils/authUtils'
import {
  clearLastActivityAt,
  getLastActivityAt,
  isSessionInactive,
  setLastActivityAt,
} from '../utils/lastActivity'
import {
  TOKEN_REFRESH_LEEWAY_SECONDS,
  TOKEN_REFRESH_POLL_MS,
} from './sessionConfig'

const BASE_URL = import.meta.env.VITE_API_URL || '/api'
const LOGIN_PATH = '/login'

const ACCESS_TOKEN_KEY = 'accessToken'
const REFRESH_TOKEN_KEY = 'refreshToken'
const PLATFORM_TOKEN_KEY = 'platformToken'
const PLATFORM_USER_KEY = 'platformUser'

const publicApi = axios.create({
  baseURL: BASE_URL,
})

const protectedApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
})

const platformApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
})

platformApi.interceptors.request.use(config => {
  const token = localStorage.getItem(PLATFORM_TOKEN_KEY)
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

const convertToMySQLDatetime = isoString => {
  const date = moment(isoString)
  return date.toISOString().replace('T', ' ').split('.')[0]
}

const isTokenExpiredOrExpiringSoon = token => {
  if (!token) return true
  if (!TOKEN_REFRESH_LEEWAY_SECONDS) return true

  try {
    const decoded = jwtDecode(token)
    const timeUntilExpiry = decoded.exp - moment().valueOf() / 1000
    return timeUntilExpiry <= TOKEN_REFRESH_LEEWAY_SECONDS
  } catch {
    return true
  }
}

const isAccessTokenExpired = token => {
  if (!token) return true

  try {
    const decoded = jwtDecode(token)
    if (!decoded?.exp) return true
    return decoded.exp <= moment().valueOf() / 1000
  } catch {
    return true
  }
}

const getUserRole = token => {
  if (!token) return null

  try {
    return jwtDecode(token).role
  } catch {
    return null
  }
}

let refreshPromise = null

const AUTH_SESSION_CODES = new Set([
  'TOKEN_RELOGIN_REQUIRED',
  'TOKEN_INVALID',
  'UNAUTHORIZED',
])

const PERMISSION_DENIED_CODES = new Set([
  'PERMISSION_DENIED',
  'ROUTE_ACCESS_DENIED',
  'ACTION_NOT_ALLOWED',
])

const isLoginOrRefreshRequest = config => {
  const url = String(config?.url || '')
  return /\/login(?:\?|$|\/)/i.test(url) || /refresh-token/i.test(url)
}

const isRefreshTokenExpired = token => {
  if (!token) return true

  try {
    const decoded = jwtDecode(token)
    if (!decoded?.exp) return false
    return decoded.exp <= moment().valueOf() / 1000
  } catch {
    return true
  }
}

const isPermissionDeniedError = error => {
  const code = error.response?.data?.code
  return PERMISSION_DENIED_CODES.has(code)
}

const isAuthSessionError = error => {
  const status = error.response?.status
  if (status !== 401 && status !== 403) return false
  if (isPermissionDeniedError(error)) return false

  const data = error.response?.data || {}
  if (AUTH_SESSION_CODES.has(data.code)) return true
  if (data.requiresReLogin === true) return true
  if (status === 401) return true

  const message = String(data.message || data.error || '').toLowerCase()
  return (
    message.includes('expiré') ||
    message.includes('expired') ||
    message.includes('jeton') ||
    message.includes('token') ||
    message.includes('reconnecter') ||
    message.includes('unauthor') ||
    message.includes('non authentifi')
  )
}

const refreshTokens = async (refreshToken, role) => {
  let endpoint

  switch (role) {
    case 'admin':
      endpoint = '/users/refresh-token'
      break
    case 'affilie':
      endpoint = '/clients/affilie/refresh-token'
      break
    case 'client':
      endpoint = '/partners/refresh-token'
      break
    default:
      throw new Error('Role non reconnu')
  }

  const response = await axios.post(`${BASE_URL}${endpoint}`, {
    refreshToken,
  })

  const { accessToken, refreshToken: newRefreshToken } = response.data

  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken)

  return { accessToken, refreshToken: newRefreshToken }
}

const refreshTokensShared = async (refreshToken, role) => {
  if (!refreshPromise) {
    refreshPromise = refreshTokens(refreshToken, role).finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

let isForceLoggingOut = false
let refreshInterval = null

const forceLogout = () => {
  if (isForceLoggingOut) return
  if (typeof window === 'undefined') return

  const isOnLoginPage = window.location.pathname === LOGIN_PATH
  isForceLoggingOut = true

  try {
    clearAuthSession()
  } catch {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  }

  try {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
  } catch {
    // ignore
  }

  if (isOnLoginPage) {
    isForceLoggingOut = false
    return
  }

  try {
    window.location.replace(LOGIN_PATH)
  } catch {
    window.location.href = LOGIN_PATH
  }
}

const tryRefreshAccessToken = async () => {
  if (isForceLoggingOut) return null

  if (isSessionInactive()) {
    forceLogout()
    return null
  }

  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)?.trim()
  // Closed browser / abandoned tab with no activity stamp: do not silent-refresh
  if (!getLastActivityAt() && isAccessTokenExpired(accessToken)) {
    forceLogout()
    return null
  }

  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
  if (!refreshToken || isRefreshTokenExpired(refreshToken)) {
    throw new Error('Session expirée. Veuillez vous reconnecter.')
  }

  const role = getUserRole(refreshToken)
  if (!role) {
    throw new Error('Session expirée. Veuillez vous reconnecter.')
  }

  const { accessToken: nextAccessToken } = await refreshTokensShared(refreshToken, role)
  return nextAccessToken?.trim() || null
}

protectedApi.interceptors.request.use(
  async config => {
    if (isForceLoggingOut) {
      return Promise.reject(new Error('Session expirée. Veuillez vous reconnecter.'))
    }

    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)

    if (!accessToken) {
      return config
    }

    if (isSessionInactive()) {
      forceLogout()
      return Promise.reject(new Error('Session expirée pour inactivité.'))
    }

    const trimmedToken = accessToken.trim()
    if (!trimmedToken || trimmedToken.length < 10) {
      return config
    }

    if (!config._retry && isTokenExpiredOrExpiringSoon(trimmedToken)) {
      try {
        const newAccessToken = await tryRefreshAccessToken()
        if (newAccessToken) {
          config.headers.Authorization = `Bearer ${newAccessToken}`
        } else {
          forceLogout()
          return Promise.reject(new Error('Session expirée. Veuillez vous reconnecter.'))
        }
      } catch {
        forceLogout()
        return Promise.reject(new Error('Session expirée. Veuillez vous reconnecter.'))
      }
    } else if (!config._retry || !config.headers?.Authorization) {
      config.headers.Authorization = `Bearer ${trimmedToken}`
    }

    config.headers = applyTraceHeader(config.headers || {})
    return config
  },
  error => Promise.reject(error)
)

protectedApi.interceptors.request.use(config => {
  if (config.method === 'put') {
    if (config.data) {
      config.data.utime = convertToMySQLDatetime(moment().toISOString())
    } else {
      config.data = { utime: convertToMySQLDatetime(moment().toISOString()) }
    }
  }
  return config
})

protectedApi.interceptors.response.use(response => {
  captureTraceFromResponse(response)
  return response
})

protectedApi.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config

    if (!originalRequest) {
      return Promise.reject(error)
    }

    if (isLoginOrRefreshRequest(originalRequest) || !isAuthSessionError(error)) {
      return Promise.reject(error)
    }

    if (originalRequest._retry || isForceLoggingOut) {
      forceLogout()
      return Promise.reject(error)
    }

    originalRequest._retry = true

    try {
      const newAccessToken = await tryRefreshAccessToken()
      if (newAccessToken) {
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return protectedApi(originalRequest)
      }
    } catch {
      // Refresh token expired or session can no longer be refreshed
    }

    forceLogout()
    return Promise.reject(error)
  }
)

export const setTokens = (accessToken, refreshToken) => {
  isForceLoggingOut = false
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  setLastActivityAt(moment().valueOf())
}

export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  clearLastActivityAt()
}

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY)
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY)

/**
 * Ensures a usable access token before protected UI renders.
 * Refuses refresh when inactive too long (browser closed counts).
 */
export const ensureValidAccessToken = async () => {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)?.trim()
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)

  if (!accessToken) return false

  if (isSessionInactive()) {
    clearTokens()
    return false
  }

  try {
    jwtDecode(accessToken)
  } catch {
    clearTokens()
    return false
  }

  if (!getLastActivityAt() && isAccessTokenExpired(accessToken)) {
    clearTokens()
    return false
  }

  if (!isTokenExpiredOrExpiringSoon(accessToken)) {
    return true
  }

  if (!refreshToken) {
    clearTokens()
    return false
  }

  try {
    const nextAccessToken = await tryRefreshAccessToken()
    if (!nextAccessToken || isAccessTokenExpired(nextAccessToken)) {
      clearTokens()
      return false
    }
    return true
  } catch {
    clearTokens()
    return false
  }
}

export const getPlatformToken = () => localStorage.getItem(PLATFORM_TOKEN_KEY)

export const setPlatformAuth = (accessToken, user) => {
  localStorage.setItem(PLATFORM_TOKEN_KEY, accessToken)
  localStorage.setItem(PLATFORM_USER_KEY, JSON.stringify(user || {}))
}

export const clearPlatformAuth = () => {
  localStorage.removeItem(PLATFORM_TOKEN_KEY)
  localStorage.removeItem(PLATFORM_USER_KEY)
}

export { BASE_URL, protectedApi, publicApi, platformApi }

export const startProactiveTokenRefresh = () => {
  if (!TOKEN_REFRESH_POLL_MS) return

  if (refreshInterval) {
    clearInterval(refreshInterval)
  }

  refreshInterval = setInterval(async () => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!accessToken || !refreshToken) return

    if (isSessionInactive()) {
      forceLogout()
      return
    }

    if (!isTokenExpiredOrExpiringSoon(accessToken)) return

    try {
      const nextAccessToken = await tryRefreshAccessToken()
      if (!nextAccessToken) forceLogout()
    } catch {
      forceLogout()
    }
  }, TOKEN_REFRESH_POLL_MS)
}

export const stopProactiveTokenRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
}
